import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FileText, LogIn, Users } from 'lucide-react'
import {
  APPLY_NOW_SHELL_LABELS,
  AUTH_CONTENT,
} from '../constants/authContent'
import { ApplyNowShell } from '../components/ApplyNowShell'
import { ApplyChoiceOption } from '../components/ApplyChoiceOption'
import { ApplyStepHeader } from '../components/ApplyStepHeader'
import type { InviteChoice, InviteCodeQuestionProps } from '../types'

const copy = AUTH_CONTENT.applyNow.step2

/**
 * Mirrors Angular register `appliedyesinfo` tab.
 * Content from AUTH_CONTENT; UI via presentational components.
 */
export function InviteCodeQuestion({ onContinue }: InviteCodeQuestionProps = {}) {
  const navigate = useNavigate()
  const [choice, setChoice] = useState<InviteChoice>('yes')

  const handleContinue = () => {
    // `used` always leaves the wizard for /login, so the parent only owns no/yes.
    if (onContinue && choice !== 'used') {
      onContinue(choice)
      return
    }
    if (choice === 'no') {
      navigate('/apply-now/need-to-apply-first')
      return
    }
    if (choice === 'used') {
      navigate('/login')
      return
    }
    navigate('/apply-now/enter-invite-code')
  }

  return (
    <ApplyNowShell
      labels={APPLY_NOW_SHELL_LABELS}
      onContinue={handleContinue}
    >
      <ApplyStepHeader title={copy.title} description={copy.description} />

      <div
        role="radiogroup"
        aria-label={copy.title}
        className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 sm:items-stretch sm:gap-3"
      >
        <ApplyChoiceOption
          name="invite-code"
          value="no"
          layout="grid"
          selected={choice === 'no'}
          onSelect={() => setChoice('no')}
          title={copy.noOption}
          description={copy.noDescription}
          icon={<FileText className="h-4 w-4" aria-hidden="true" />}
        />
        <ApplyChoiceOption
          name="invite-code"
          value="yes"
          layout="grid"
          selected={choice === 'yes'}
          onSelect={() => setChoice('yes')}
          title={copy.yesOption}
          description={copy.yesDescription}
          icon={<Users className="h-4 w-4" aria-hidden="true" />}
        />
        <div className="sm:col-span-2">
          <ApplyChoiceOption
            name="invite-code"
            value="used"
            selected={choice === 'used'}
            onSelect={() => setChoice('used')}
            title={copy.yesUsedOption}
            description={copy.yesUsedDescription}
            icon={<LogIn className="h-4 w-4" aria-hidden="true" />}
          />
        </div>
      </div>
    </ApplyNowShell>
  )
}
